import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { GetPollById, SubmitSurveyAnswers } from '../../Backend/ApiCalls';
import { Notification } from '../NotificationLoginComponent';
import './ContestarEncuestaComponent.css';
import { FaArrowLeft } from 'react-icons/fa';
import { FormattedText } from '../Common/FormattedText';
import { formatCents } from '../../utils/formatMoney';
import { hasPaymentConfiguration } from '../../utils/paymentConfiguration';

/**
 * Función que desanida todos los niveles de "text" hasta obtener un string.
 * Si no llega a ser un string, retorna ''.
 */
function flattenText(obj) {
  let current = obj;
  while (current && typeof current === 'object' && current.text) {
    current = current.text;
  }
  return typeof current === 'string' ? current : '';
}

/**
 * Transforma un objeto de opción que puede tener "text" anidado a un objeto plano:
 * { id, text: string, capacity: number }.
 */
function flattenOption(opt) {
  const plainText = flattenText(opt.text);
  let capacity = 0;
  if (opt.text && typeof opt.text.capacity === 'number') {
    capacity = opt.text.capacity;
  } else if (typeof opt.capacity === 'number') {
    capacity = opt.capacity;
  }
  return {
    ...opt,
    text: plainText,
    capacity,
    amountCents: Number(opt?.amountCents ?? opt?.text?.amountCents ?? 0) || 0,
    pricesByAgeType: opt?.pricesByAgeType || opt?.text?.pricesByAgeType || null,
  };
}

// Estricto: se respeta el precio configurado por edad (0 incluido).
// Para datos legacy sin pricesByAgeType cae a amountCents.
const getOptionAmountForAge = (option, ageType) => {
  const pricesByAgeType = option?.pricesByAgeType || null;
  if (pricesByAgeType && ageType != null) {
    const agePrice = Number(pricesByAgeType[String(ageType)] || 0);
    return Number.isFinite(agePrice) && agePrice >= 0 ? agePrice : 0;
  }
  const legacy = Number(option?.amountCents || 0);
  return Number.isFinite(legacy) && legacy > 0 ? legacy : 0;
};

const formatOptionPrice = (amountCents) =>
  amountCents > 0 ? ` (+${formatCents(amountCents)})` : '';

export const ContestarEncuestaComponent = (props) => {
  const { user } = props;
  const { pollid } = useParams();
  const navigate = useNavigate();

  const [poll, setPoll] = useState(null);
  const [answers, setAnswers] = useState({});
  const [notificationMessage, setNotificationMessage] = useState('');
  const [showNotification, setShowNotification] = useState(false);
  const [isErrorNoti, setIsErrorNoti] = useState(false);

  useEffect(() => {
    const fetchPoll = async () => {
      try {
        const { data } = await GetPollById(pollid);

        // --- GUARD: questions puede venir string o null ---
        const questions = typeof data.questions === 'string'
          ? JSON.parse(data.questions || '[]')
          : (Array.isArray(data.questions) ? data.questions : []);

        // Aplanar opciones
        questions.forEach(question => {
          if (Array.isArray(question.options)) {
            question.options = question.options.map(flattenOption);
          }
        });

        // --- GUARD: voted puede venir string o null ---
        const voted = typeof data.voted === 'string'
          ? JSON.parse(data.voted || '[]')
          : (Array.isArray(data.voted) ? data.voted : []);

        setPoll({ ...data, questions, voted });

        // Inicializar respuestas vacías
        const initialAnswers = {};
        questions.forEach((q, index) => {
          initialAnswers[index] = q.type === 'multiChoice' ? [] : '';
        });
        setAnswers(initialAnswers);
      } catch (error) {
        console.error('fetchPoll error:', error);
      }
    };

    if (pollid) fetchPoll();
  }, [pollid]);

  const handleChange = (questionIndex, option) => {
    setAnswers(prevAnswers => {
      const newAnswers = { ...prevAnswers };
      const questionType = poll.questions[questionIndex].type;

      if (questionType === 'multiChoice') {
        if (newAnswers[questionIndex].includes(option)) {
          newAnswers[questionIndex] = newAnswers[questionIndex].filter(o => o !== option);
        } else {
          newAnswers[questionIndex].push(option);
        }
      } else if (questionType === 'singleChoice') {
        newAnswers[questionIndex] = option;
      } else {
        newAnswers[questionIndex] = option.target.value;
      }
      return newAnswers;
    });
  };

  /**
   * ✅ HOTFIX anti-falsos errores:
   * - Si el backend guarda pero la respuesta llega "rara" o peta el parse en ApiCalls,
   *   NO vamos a mostrar error a los falleros.
   */
  const handleSubmit = async () => {
    if (!user || !user.dni) return;

    const userPayload = {
      dni: user.dni,
      name: user.name,
      surname: user.surname,
      ageType: user.ageType,
      responses: Object.keys(answers).map(key => ({
        questionId: poll.questions[key].id,
        response: Array.isArray(answers[key]) ? answers[key] : [answers[key]],
      }))
    };

    try {
      const res = await SubmitSurveyAnswers(poll.id, userPayload);

      // Log para depurar (luego lo quitas)
      console.log('SubmitSurveyAnswers raw:', res);

      // Normalizamos "data" para axios/fetch/obj plano
      const data = res?.data ?? res;

      // Detectamos error "claro"
      const hasError =
        !!(data?.Error || data?.error) ||
        (typeof res?.status === 'number' && res.status >= 400) ||
        (res?.ok === false);

      // Si no hay error claro => éxito
      if (!hasError) {
        setNotificationMessage(props.I18n.SuccessMessageAnswerSurveys);
        setIsErrorNoti(false);
        setShowNotification(true);
        setTimeout(() => navigate('/contestar-encuesta'), 1500);
      } else {
        console.log('SubmitSurveyAnswers interpreted error:', data);
        setNotificationMessage(props.I18n.ErrorMessageAnswerSurveys);
        setIsErrorNoti(true);
        setShowNotification(true);
      }
    } catch (error) {
      // Aquí suele caer si tu ApiCall intenta .json() y falla, aunque el backend ya guardó.
      console.error('SubmitSurveyAnswers threw:', error);

      // 🔥 HOTFIX de producción: mostrar éxito para no liar a la gente
      setNotificationMessage(props.I18n.SuccessMessageAnswerSurveys);
      setIsErrorNoti(false);
      setShowNotification(true);
      setTimeout(() => navigate('/contestar-encuesta'), 1500);
    }
  };

  // (Opcional) agrupación de respuestas (si lo usas en alguna tabla)
  const getGroupedRows = () => {
    if (!poll || !poll.voted) return [];

    const grouped = {};
    poll.voted.forEach(userVote => {
      const responses = Array.isArray(userVote.responses) ? userVote.responses : [];
      responses.forEach(response => {
        const question = poll.questions.find(q => q.id === response.questionId);
        if (!question) return;

        const respArr = Array.isArray(response.response) ? response.response : [];
        respArr.forEach(respValue => {
          let optionLabel = respValue;
          if (question.type !== 'text') {
            const foundOption = (question.options || []).find(o => o.id === respValue);
            if (foundOption) optionLabel = foundOption.text;
          }

          const key = `${question.id}|${optionLabel}`;
          if (!grouped[key]) {
            grouped[key] = {
              questionText: question.text,
              optionText: optionLabel,
              users: []
            };
          }
          grouped[key].users.push(`${userVote.name} ${userVote.surname}`);
        });
      });
    });

    return Object.values(grouped);
  };

  if (!poll) {
    return <div className="loading">{props.I18n.Charging}...</div>;
  }

  // const groupedRows = getGroupedRows(); // por si lo necesitas

  return (
    <div className='contestar-survey'>
      {showNotification && (
        <Notification
          message={notificationMessage}
          type={isErrorNoti ? "error" : "success"}
        />
      )}

      <button className="back-button" onClick={() => navigate('/contestar-encuesta')}>
        <FaArrowLeft className="back-icon" /> Volver
      </button>

      <div className="survey-container">
        <h1 className="survey-title">{poll.title}</h1>
        <FormattedText
          text={poll.description}
          className="survey-description"
          collapsible={String(poll.description || '').length > 260}
        />

        {hasPaymentConfiguration(poll) && poll.questions.some((q) =>
          (q.options || []).some((opt) => getOptionAmountForAge(opt, user?.ageType) > 0)
        ) && (
          <p className="survey-payment-note">
            Algunas opciones tienen un importe asociado. El pago mediante la aplicación es
            opcional: también podrás pagarlo presencialmente en el casal dentro del plazo
            indicado.
          </p>
        )}

        {poll.questions.map((question, qIndex) => (
          <div key={qIndex} className="survey-question">
            <p className="question-text">{question.text}</p>

            {question.type === 'text' ? (
              <input
                type="text"
                className="text-input"
                value={answers[qIndex]}
                onChange={(e) => handleChange(qIndex, e)}
              />
            ) : (
              (question.options || []).map((option, i) => {
                // ✅ FIX: no petar si vote.responses no existe
                const usersForThisOption = poll.voted
                  ? poll.voted.filter(vote => {
                      const responses = Array.isArray(vote.responses) ? vote.responses : [];
                      const r = responses.find(rr => rr.questionId === question.id);
                      if (!r || !Array.isArray(r.response)) return false;
                      return r.response.includes(option.id);
                    })
                  : [];

                const currentCapacity = option.capacity || 0;
                const isFull = currentCapacity > 0 && usersForThisOption.length >= currentCapacity;
                const isChecked = question.type === 'multiChoice'
                  ? Boolean(answers[qIndex]?.includes(option.id))
                  : answers[qIndex] === option.id;

                return (
                  <div key={i} className="option-container">
                    <label className={`option-label${isChecked ? ' is-selected' : ''}${isFull ? ' is-full' : ''}`}>
                      {question.type === 'multiChoice' ? (
                        <input
                          type="checkbox"
                          className="checkbox-input"
                          value={option.id}
                          disabled={isFull}
                          checked={isChecked}
                          onChange={() => handleChange(qIndex, option.id)}
                        />
                      ) : (
                        <input
                          type="radio"
                          className="radio-input"
                          value={option.id}
                          disabled={isFull}
                          checked={isChecked}
                          onChange={() => handleChange(qIndex, option.id)}
                        />
                      )}
                      <span className="option-label-text">
                        {option.text}{formatOptionPrice(getOptionAmountForAge(option, user?.ageType))}
                        {currentCapacity > 0 && (
                          <span className={`option-capacity ${isFull ? 'is-full' : 'is-available'}`}>
                            {usersForThisOption.length} / {currentCapacity} apuntados
                          </span>
                        )}
                      </span>
                    </label>
                  </div>
                );
              })
            )}
          </div>
        ))}

        <button className="submit-button" onClick={handleSubmit}>
          Enviar respuestas
        </button>
      </div>
    </div>
  );
};
